import { useRef, useState } from 'react';
import { Plus, Tag, X } from 'lucide-react';

interface TeamTagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  /** Already-used tags across other teams, offered as one-click suggestions. */
  knownTags?: string[];
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Free-text chip input for team tags. Deliberately NOT the ticket-tagging
 * TicketTagInput/tagService — those are a separate domain (ticket-service's
 * `/tags` suggestions, scoped to ticket usage counts). Team tags are a much
 * smaller, per-tenant set with no backend suggestion endpoint of their own,
 * so suggestions here are just the distinct tags already seen on other teams
 * in the currently-loaded list.
 */
export function TeamTagInput({
  value,
  onChange,
  knownTags = [],
  disabled,
  placeholder = 'Add a tag...',
}: TeamTagInputProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const addTag = (raw: string) => {
    const tag = raw.trim();
    if (!tag || value.includes(tag)) return;
    onChange([...value, tag]);
    setQuery('');
    setOpen(false);
  };

  const removeTag = (tag: string) => {
    onChange(value.filter((t) => t !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (query.trim()) addTag(query);
    } else if (e.key === 'Backspace' && !query && value.length > 0) {
      removeTag(value[value.length - 1]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const suggestions = knownTags.filter(
    (t) => !value.includes(t) && (query.trim() ? t.toLowerCase().includes(query.trim().toLowerCase()) : true),
  );
  const exactMatch = suggestions.some((t) => t.toLowerCase() === query.trim().toLowerCase());
  const showCreateOption = query.trim().length > 0 && !exactMatch && !value.includes(query.trim());

  return (
    <div
      className="relative"
      ref={wrapperRef}
      onBlur={(e) => {
        if (!wrapperRef.current?.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      <div
        onClick={() => inputRef.current?.focus()}
        className="flex min-h-[42px] flex-wrap content-start items-center gap-1.5 rounded-lg border border-gray-300 bg-white p-2 dark:border-gray-600 dark:bg-gray-800"
      >
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-sm text-gray-700 dark:bg-gray-700 dark:text-gray-200"
          >
            <Tag className="h-3 w-3" />
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              disabled={disabled}
              className="ml-0.5 rounded-full p-0.5 hover:bg-black/10 disabled:opacity-50 dark:hover:bg-white/10"
              aria-label={`Remove tag ${tag}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={value.length === 0 ? placeholder : ''}
          className="min-w-[120px] flex-1 border-none bg-transparent text-sm text-gray-700 outline-none placeholder-gray-400 disabled:opacity-50 dark:text-gray-200 dark:placeholder-gray-500"
        />
      </div>

      {open && (suggestions.length > 0 || showCreateOption) && (
        <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {showCreateOption && (
            <button
              type="button"
              onClick={() => addTag(query)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <Plus className="h-4 w-4 text-green-500" />
              <span>
                Add <span className="font-medium">"{query.trim()}"</span>
              </span>
            </button>
          )}
          {suggestions.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => addTag(t)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <Tag className="h-3.5 w-3.5 text-gray-400" />
              {t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
